import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { FileText, ChevronRight } from "lucide-react";
import SafeHtml from "@/components/SafeHtml";

export function PolicyList() {
  const { t, lang, pick } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/policies").then((r) => setItems(
    (r.data || []).filter((p) => p.published !== false && p.is_archived !== true)
  )); }, []);

  return (
    <div data-testid="policies-page">
      <section className="bg-navy text-cream py-24">
        <div className="container-x">
          <span className="eyebrow">{lang === "ar" ? "الحوكمة الذاتية" : "Platform Self-Governance"}</span>
          <h1 className="mt-4 text-4xl md:text-6xl">
            {lang === "ar" ? "وثائق حوكمة المنصة" : "Platform Governance Documents"}
          </h1>
          <span className="divider-gold" />
          <p className="text-cream/80 max-w-2xl leading-relaxed">
            {lang === "ar"
              ? "Hawkam تُطبّق الحوكمة على منصتها الخاصة كنموذج عملي لعملائها — عشر وثائق رسمية، محدّثة برقم إصدار وسجل تعديلات."
              : "Hawkam applies governance to its own platform as a live demonstration for clients — ten official documents with version numbers and full history."}
          </p>
        </div>
      </section>

      <section className="container-x py-16 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((p) => (
          <Link
            key={p.key}
            to={`/policies/${p.key}`}
            data-testid={`policy-${p.key}`}
            className="card-elev p-6 flex items-start gap-4 hover:border-gold group transition-colors"
          >
            <FileText size={22} className="text-gold flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h2 className="text-lg text-navy">{pick(p, "title")}</h2>
              <div className="mt-1 text-xs uppercase tracking-widest text-navy/60">
                {t("common.version")} {p.version}
              </div>
            </div>
            <ChevronRight size={18} className="text-navy/50 group-hover:text-gold" />
          </Link>
        ))}
      </section>
    </div>
  );
}

export function PolicyDetail() {
  const { key } = useParams();
  const { lang, pick } = useI18n();
  const [policy, setPolicy] = useState(null);
  useEffect(() => { api.get(`/policies/${key}`).then((r) => setPolicy(r.data)).catch(() => setPolicy(false)); }, [key]);

  if (policy === null) return <div className="container-x py-24 text-navy/60">Loading…</div>;
  if (policy === false) return <div className="container-x py-24 text-navy/60">Not found.</div>;

  return (
    <div className="container-x py-16 max-w-4xl" data-testid="policy-detail">
      <Link to="/policies" className="text-navy/70 hover:text-gold text-sm">← {lang === "ar" ? "كل الوثائق" : "All documents"}</Link>
      <h1 className="mt-4 text-4xl md:text-5xl text-navy">{pick(policy, "title")}</h1>
      <div className="mt-2 flex items-center gap-4 text-xs uppercase tracking-widest text-navy/60">
        <span>Version {policy.version}</span>
        <span>Updated {new Date(policy.updated_at).toLocaleDateString(lang === "ar" ? "ar" : "en")}</span>
      </div>
      <span className="divider-gold" />
      <SafeHtml className="prose max-w-none text-navy/85 leading-relaxed" html={pick(policy, "body")} />
    </div>
  );
}
